import { useState } from 'react'
import { Copy, Download, FileText, Loader2, Sparkles } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../lib/api'
import Modal from '../ui/Modal'

interface Props {
  open: boolean
  onClose: () => void
  opportunityId: string
  opportunityCode: string
  opportunityTitle: string
  clientId: string
}

interface GenerateResponse {
  markdown: string
  conversationId: string
}

interface CreatedQuoteResponse {
  quote: { id: string }
}

export default function ProposalGeneratorModal({
  open,
  onClose,
  opportunityId,
  opportunityCode,
  opportunityTitle,
  clientId,
}: Props) {
  const { t } = useTranslation(['ai', 'common'])
  const [busy, setBusy] = useState(false)
  const [creatingQuote, setCreatingQuote] = useState(false)
  const [markdown, setMarkdown] = useState('')
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [warn, setWarn] = useState<string | null>(null)
  const [showPreview, setShowPreview] = useState(true)
  const [createdQuoteId, setCreatedQuoteId] = useState<string | null>(null)

  const reset = () => {
    setMarkdown('')
    setConversationId(null)
    setError(null)
    setWarn(null)
    setCreatedQuoteId(null)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const onGenerate = async () => {
    setBusy(true)
    setError(null)
    setWarn(null)
    setCreatedQuoteId(null)
    try {
      const resp = await api.post<GenerateResponse>(
        `/ai/generate-proposal/${opportunityId}`
      )
      setMarkdown(resp.markdown)
      setConversationId(resp.conversationId)
    } catch (err) {
      handleError(err)
    } finally {
      setBusy(false)
    }
  }

  const handleError = (err: unknown) => {
    if (err instanceof ApiError) {
      const body = err.body as { error?: string; code?: string } | undefined
      if (body?.code === 'AI_NOT_CONFIGURED') {
        setWarn(t('proposalGenerator.errors.aiNotConfigured'))
        return
      }
      setError(body?.error ?? err.message)
      return
    }
    setError(err instanceof Error ? err.message : t('proposalGenerator.errors.unexpected'))
  }

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(markdown)
    } catch {
      /* noop */
    }
  }

  const onDownload = () => {
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `propuesta-${opportunityCode}.md`
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }

  const onCreateQuote = async () => {
    if (!markdown.trim()) return
    setCreatingQuote(true)
    setError(null)
    try {
      const resp = await api.post<CreatedQuoteResponse>('/quotes', {
        clientId,
        opportunityId,
        title: opportunityTitle,
        notes: markdown,
        lines: [],
      })
      setCreatedQuoteId(resp.quote.id)
    } catch (err) {
      handleError(err)
    } finally {
      setCreatingQuote(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`${t('proposalGenerator.title')} — ${opportunityCode}`}
      description={t('proposalGenerator.description')}
      maxWidth="max-w-4xl"
    >
      <div className="flex flex-col gap-4 max-h-[75vh]">
        {warn && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-200 text-xs px-3 py-2">
            {warn}
          </div>
        )}
        {error && (
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-200 text-xs px-3 py-2">
            {error}
          </div>
        )}

        {!markdown && !busy && (
          <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
            <Sparkles size={28} className="text-jr2-400" />
            <p className="text-sm t-secondary max-w-md">
              {t('proposalGenerator.intro')}
            </p>
            <button
              type="button"
              onClick={onGenerate}
              disabled={busy}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 disabled:opacity-50 flex items-center gap-1.5"
            >
              <Sparkles size={14} /> {t('proposalGenerator.generate')}
            </button>
          </div>
        )}

        {busy && (
          <div className="flex flex-col items-center justify-center gap-2 py-12 t-muted text-sm">
            <Loader2 size={22} className="animate-spin text-jr2-400" />
            {t('proposalGenerator.drafting')}
          </div>
        )}

        {markdown && !busy && (
          <>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 text-xs">
                <button
                  type="button"
                  onClick={() => setShowPreview(true)}
                  className={`px-2.5 py-1 rounded-lg border ${
                    showPreview
                      ? 'border-jr2-500/60 bg-jr2-500/10 t-primary'
                      : 'border-base t-secondary hover:bg-hover'
                  }`}
                >
                  {t('proposalGenerator.tabs.preview')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowPreview(false)}
                  className={`px-2.5 py-1 rounded-lg border ${
                    !showPreview
                      ? 'border-jr2-500/60 bg-jr2-500/10 t-primary'
                      : 'border-base t-secondary hover:bg-hover'
                  }`}
                >
                  {t('proposalGenerator.tabs.editMarkdown')}
                </button>
              </div>
              <button
                type="button"
                onClick={onGenerate}
                disabled={busy}
                className="text-xs px-2.5 py-1 rounded-lg border border-base hover:bg-hover t-secondary flex items-center gap-1.5"
              >
                <Sparkles size={12} /> {t('proposalGenerator.regenerate')}
              </button>
            </div>

            {showPreview ? (
              <div className="flex-1 min-h-0 overflow-y-auto pr-1 prose prose-sm prose-invert max-w-none surface p-4">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
              </div>
            ) : (
              <textarea
                value={markdown}
                onChange={(e) => setMarkdown(e.target.value)}
                rows={18}
                className="flex-1 min-h-0 w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary font-mono text-xs focus:outline-none focus:ring-1 focus:ring-jr2-500/40"
              />
            )}

            {createdQuoteId && (
              <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200 text-xs px-3 py-2 flex items-center justify-between gap-2">
                <span>{t('proposalGenerator.quoteCreated')}</span>
                <a
                  href={`/quotes/${createdQuoteId}`}
                  className="underline hover:text-white"
                >
                  {t('proposalGenerator.open')}
                </a>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 flex-wrap">
              {conversationId && (
                <a
                  href={`/ai?conversation=${conversationId}`}
                  className="px-3 py-1.5 rounded-lg text-xs t-secondary border border-base hover:bg-hover flex items-center gap-1.5"
                >
                  {t('proposalGenerator.openInChat')}
                </a>
              )}
              <button
                type="button"
                onClick={onCopy}
                className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover flex items-center gap-1.5"
              >
                <Copy size={14} /> {t('proposalGenerator.copy')}
              </button>
              <button
                type="button"
                onClick={onDownload}
                className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover flex items-center gap-1.5"
              >
                <Download size={14} /> {t('proposalGenerator.downloadMd')}
              </button>
              <button
                type="button"
                onClick={onCreateQuote}
                disabled={creatingQuote || createdQuoteId != null}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 disabled:opacity-50 flex items-center gap-1.5"
              >
                {creatingQuote ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <FileText size={14} />
                )}
                {t('proposalGenerator.createDraftQuote')}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
